import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {onClose && (
          <Button
            onClick={onClose}
            variant="outline"
            className="mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
        )}

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: December 19, 2025</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                TOR1 Services ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our Transfer of Residence services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.1 Personal Information</h3>
              <p className="mb-3">When you contact us or use our services, we may collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Country of current residence and relocation details</li>
                <li>Information about personal belongings and furniture</li>
                <li>Proof of UK nationality and residence documentation</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.2 Automatically Collected Information</h3>
              <p className="mb-3">When you visit our website, we may automatically collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Process your TOR1 Transfer of Residence applications</li>
                <li>Communicate with you about your relocation and customs clearance</li>
                <li>Prepare required customs documentation for HMRC and UK Border Force</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve our website and services</li>
                <li>Send service updates and relevant information (with your consent)</li>
                <li>Comply with legal obligations and customs regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p className="mb-3">We process your personal data under the following legal bases:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Contract Performance:</strong> Processing your TOR1 application and providing relocation services</li>
                <li><strong>Legal Obligation:</strong> Compliance with HMRC and UK customs regulations</li>
                <li><strong>Legitimate Interest:</strong> Improving our services and communicating with clients</li>
                <li><strong>Consent:</strong> Marketing communications (where applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Sharing and Disclosure</h2>
              <p className="mb-3">We may share your information with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>UK Government Authorities:</strong> HMRC and UK Border Force for customs clearance</li>
                <li><strong>Removal Companies:</strong> Logistics partners assisting with your relocation (with your consent)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
              </ul>
              <p className="mt-3">
                We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. TOR1 documentation is typically retained for 7 years in compliance with HMRC requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
              <p className="mb-3">Under GDPR and UK data protection laws, you have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> Limit how we use your data</li>
                <li><strong>Data Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at <a href="mailto:info@tor1services.com" className="text-blue-900 hover:underline">info@tor1services.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
              <p className="mb-3">
                Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device. We use:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              </ul>
              <p className="mt-3">
                You can control cookies through your browser settings. Disabling cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p>
                Your information may be processed in countries outside the UK/EU. We ensure appropriate safeguards are in place to protect your data in accordance with GDPR requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
              <p className="mb-3">If you have questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">TOR1 Services</p>
                <p>Email: <a href="mailto:info@tor1services.co.uk" className="text-blue-900 hover:underline">info@tor1services.co.uk</a></p>
                <p>Phone: <a href="tel:0034699913354" className="text-blue-900 hover:underline">0034 699 913 354</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Supervisory Authority</h2>
              <p>
                If you are unhappy with how we handle your personal data, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):
              </p>
              <div className="bg-gray-100 p-6 rounded-lg mt-3">
                <p className="font-semibold text-gray-900 mb-2">Information Commissioner's Office</p>
                <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-900 hover:underline">ico.org.uk</a></p>
                <p>Phone: 0303 123 1113</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
